/* eslint-disable react/function-component-definition */
import React, { ElementType, useMemo } from 'react';
import clsx from 'clsx';
import { Spinner, SpinnerProps, SpinnerSize } from '../Spinner';
import { IconSize } from '../Icon/iconSize';
import { Icon } from '../Icon';

import { useButtonBubbles } from './useButtonBubbles';

export enum ButtonSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export enum ButtonVariant {
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  tertiaryInvert = 'tertiaryInvert'
}

export enum SpinnerPosition {
  default = 'default',
  left = 'left',
  right = 'right'
}

export type ButtonProps<ComponentType extends ElementType> = {
  /**
   * Тип компонента кнопки
   *
   * @type React.ElementType
   */
  component?: ComponentType;
  /**
   * Размер ("small", "medium", "large")
   */
  size?: ButtonSize;
  /**
   * Вариант оформления ("primary", "secondary", "tertiary", "tertiaryInvert")
   */
  variant?: ButtonVariant;
  /**
   * Неактивное состояние
   */
  inactive?: boolean;
  /**
   * Заблокированное состояние
   */
  disabled?: boolean;
  /**
   * Выбранное состояние
   */
  selected?: boolean;
  /**
   * Цвет в hex-формате
   */
  color?: string;
  /**
   * Иконка слева
   */
  leftIcon?: React.ElementType;
  /**
   * Иконка справа
   */
  rightIcon?: React.ElementType;
  /**
   * Слот слева. Приоритетнее, чем иконка
   */
  left?: React.ReactNode;
  /**
   * Слот справа. Приоритетнее, чем иконка
   */
  right?: React.ReactNode;
  /**
   * Состояние спиннера.
   * Если true, то рисует indeterminate спиннер
   * Если число, то это процент заполнения (от 0 до 100).
   * Если 'done', то рисует галочку
   */
  spinner?: boolean | number | 'done';
  /**
   * Положение спиннера ("default", "left", "right")
   */
  spinnerPosition?: SpinnerPosition;
  /**
   * Потомки компонента
   */
  children?: React.ReactNode;
  /**
   * Реф на корневой DOM-элемент
   */
  innerRef?: React.Ref<HTMLElement>;
  /**
   * Дополнительные css-классы элементов
   */
  classes?: {
    bubbleRoot?: string;
    content?: string;
    contentLeft?: string;
    contentRight?: string;
    spinner?: string;
    text?: string;
    icon?: string;
  };
} & Omit<React.ComponentProps<ComponentType>, 'size'>;

/**
 * @deprecated используйте ButtonProps
 */
export type IButtonProps<ComponentType extends React.ElementType> = ButtonProps<
  ComponentType
>;

export type ButtonPropsDefault = Omit<ButtonProps<'button'>, 'component'> & {
  /**
   * Тип компонента кнопки
   *
   * @type React.ElementType
   */
  component?: 'button';
};

const ButtonBase = <ComponentType extends ElementType>({
  component,
  className,
  style,
  size = ButtonSize.medium,
  variant = ButtonVariant.primary,
  inactive,
  disabled,
  selected,
  children,
  spinnerPosition = SpinnerPosition.default,
  spinner,
  leftIcon,
  right,
  left,
  rightIcon,
  color,
  innerRef,
  classes,
  ...props
}: ButtonProps<ComponentType>) => {
  const [bubbleRootRef, bubblesProps] = useButtonBubbles<ComponentType>(
    !disabled && !inactive,
    props
  );

  // Чтобы ts не проверял корректность передачи пропов
  const Component = component as React.ElementType;

  const spinnerProps = useMemo(() => {
    const props: SpinnerProps = {
      className: 'hse-Button__spinner',
      size: size === ButtonSize.small ? SpinnerSize.small : SpinnerSize.medium
    };

    if (spinner === true) {
      props.loading = true;
    } else if (spinner === 'done') {
      props.done = true;
    } else if (typeof spinner === 'number') {
      props.value = spinner;
    }

    return props;
  }, [size, spinner]);

  const iconSize = size === ButtonSize.small ? IconSize.small : IconSize.medium;

  const spinnerComponent = (
    <Spinner
      className={clsx(
        'hse-Button__spinner',
        'hse-Spinner_without-done-fill-animation',
        classes?.spinner,
        {
          'hse-Button__spinner_left': spinnerPosition === SpinnerPosition.left
        }
      )}
      {...spinnerProps}
    />
  );
  const isSpinnerDefault =
    spinner &&
    spinner !== 'done' &&
    spinnerPosition === SpinnerPosition.default;

  const hasLeft =
    !isSpinnerDefault &&
    (left || leftIcon || (spinner && spinnerPosition === SpinnerPosition.left));
  const hasRight =
    !isSpinnerDefault &&
    (right ||
      rightIcon ||
      (spinner && spinnerPosition === SpinnerPosition.right));

  return (
    <Component
      ref={innerRef}
      className={clsx(className, 'hse-Button', {
        [`hse-Button_size_${size}`]: size,
        [`hse-Button_variant_${variant}`]: variant,
        [`hse-Button_disabled`]: disabled,
        [`hse-Button_inactive`]: inactive,
        'hse-Button_selected': selected,
        'hse-Button_custom-color': Boolean(color),
        'hse-Button_hasSpinner': spinner && spinnerPosition,
        'hse-Button_hasLeft': hasLeft,
        'hse-Button_hasRight': hasRight,
        'hse-Button_hasSideContent': hasLeft || hasRight,
        [`hse-Button_spinner_${spinner}`]: spinner
      })}
      type="button"
      style={{
        color,
        ...style
      }}
      {...props}
      {...bubblesProps}
    >
      <div
        ref={bubbleRootRef}
        className={clsx('hse-Button__bubbleRoot', classes?.bubbleRoot)}
      />
      <div className={clsx('hse-Button__content', classes?.content)}>
        {hasLeft && (
          <div
            className={clsx('hse-Button__contentLeft', classes?.contentLeft)}
          >
            {left || (
              <>
                {!spinner && leftIcon ? (
                  <Icon
                    size={iconSize}
                    className={clsx(
                      'hse-Button__icon',
                      'hse-Button__icon_left',
                      classes?.icon
                    )}
                    icon={leftIcon}
                  />
                ) : spinner && spinnerPosition === SpinnerPosition.left ? (
                  spinnerComponent
                ) : null}
              </>
            )}
          </div>
        )}

        {isSpinnerDefault ? (
          spinnerComponent
        ) : (
          <div className={clsx('hse-Button__text', classes?.text)}>{children}</div>
        )}

        {hasRight && (
          <div
            className={clsx('hse-Button__contentRight', classes?.contentRight)}
          >
            {right || (
              <>
                {' '}
                {!spinner && rightIcon ? (
                  <Icon
                    size={iconSize}
                    className={clsx(
                      'hse-Button__icon',
                      'hse-Button__icon_right',
                      classes?.icon
                    )}
                    icon={rightIcon}
                  />
                ) : spinner && spinnerPosition === SpinnerPosition.right ? (
                  spinnerComponent
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
    </Component>
  );
};

// Шаманство с типами, чтобы можно было писать <Button /> без указания component

export function Button(props: ButtonPropsDefault);
export function Button<ComponentType extends ElementType>(
  props: ButtonProps<ComponentType>
);
export function Button<ComponentType extends ElementType>(
  props: ButtonProps<ComponentType> | ButtonPropsDefault
) {
  if (!props.component) {
    props = props as ButtonPropsDefault;
    return <ButtonBase<'button'> {...props} component={'button'} />;
  }
  props = props as ButtonProps<ComponentType>;
  return <ButtonBase {...props} />;
}

Button.Variant = ButtonVariant;
Button.Size = ButtonSize;
Button.SpinnerPosition = SpinnerPosition;
Button.SpinnerSize = SpinnerSize;
